"use client"

import { useEffect } from 'react'

/**
 * 处理 chunk 加载失败的全局错误处理器
 * 当检测到 chunk 加载失败时，自动刷新页面以获取最新版本
 *
 * 防死循环：
 * - 本会话累计自动刷新次数上限（不随时间窗口重置而“清零配额”）
 * - 同一页面生命周期内最多调度 1 次刷新（避免一次失败触发多条 error/rejection 连刷）
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const MAX_BURST = 2
    const RETRY_WINDOW_MS = 30 * 1000
    const MAX_SESSION_RELOADS = 5
    const RELOAD_KEY = 'chunk-error-last-refresh'
    const RETRY_COUNT_KEY = 'chunk-error-retry-count'
    const SESSION_TOTAL_KEY = 'chunk-error-session-reloads'

    let reloadScheduled = false

    const isChunkError = (message: string) =>
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed')

    const isNextChunkScript = (src: string) =>
      src.includes('/_next/static/chunks/') ||
      src.includes('/_next/static/css/')

    const reloadWithCacheBusting = () => {
      const nextUrl = new URL(window.location.href)
      nextUrl.searchParams.set('__chunk_retry', String(Date.now()))
      window.location.assign(nextUrl.toString())
    }

    const tryRecoverFromChunkError = (errorMessage: string) => {
      if (reloadScheduled) {
        return
      }

      if (!navigator.onLine) {
        console.warn('检测到 chunk 加载失败，但当前网络离线，暂不自动刷新:', errorMessage)
        return
      }

      const sessionTotal = Number(sessionStorage.getItem(SESSION_TOTAL_KEY) || '0')
      if (sessionTotal >= MAX_SESSION_RELOADS) {
        console.warn(
          'chunk 自动恢复已达本会话上限（',
          MAX_SESSION_RELOADS,
          ' 次），停止自动刷新，请手动刷新或检查部署/CDN：',
          errorMessage,
        )
        return
      }

      const now = Date.now()
      const lastRefresh = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
      const previousRetryCount = Number(sessionStorage.getItem(RETRY_COUNT_KEY) || '0')
      const inRetryWindow = lastRefresh > 0 && now - lastRefresh < RETRY_WINDOW_MS
      const retryCount = inRetryWindow ? previousRetryCount : 0

      if (retryCount >= MAX_BURST) {
        console.warn('chunk 短窗口内自动恢复次数已达上限，停止自动刷新:', errorMessage)
        return
      }

      reloadScheduled = true
      sessionStorage.setItem(SESSION_TOTAL_KEY, String(sessionTotal + 1))
      sessionStorage.setItem(RELOAD_KEY, String(now))
      sessionStorage.setItem(RETRY_COUNT_KEY, String(retryCount + 1))
      console.warn(`检测到 chunk 加载失败，执行第 ${sessionTotal + 1} 次会话级自动恢复:`, errorMessage)

      setTimeout(() => {
        reloadWithCacheBusting()
      }, 120)
    }

    const handleChunkError = (event: Event) => {
      const ev = event as ErrorEvent
      const message = ev.message || ev.error?.message || ''
      const script = ev.target instanceof HTMLScriptElement ? ev.target : null
      const src = script?.src || ev.filename || ''

      const fromMessage = isChunkError(message)
      const fromNextScript =
        Boolean(src) && isNextChunkScript(src)

      if (!fromMessage && !fromNextScript) {
        return
      }

      tryRecoverFromChunkError(message || src || 'chunk script load error')
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const errorMessage =
        typeof reason === 'string'
          ? reason
          : reason?.message || ''

      if (isChunkError(errorMessage)) {
        event.preventDefault()
        tryRecoverFromChunkError(errorMessage)
      }
    }

    window.addEventListener('error', handleChunkError, true)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleChunkError, true)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return null
}

